import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText as GSAPSplitText } from 'gsap/SplitText';
import React, { useEffect, useRef } from 'react';

gsap.registerPlugin(ScrollTrigger, GSAPSplitText);

interface StyledWord {
  text: string;
  className?: string;
}

interface StyledSplitTextProps {
  words: StyledWord[];
  className?: string;
  delay?: number;
  duration?: number;
  // eslint-disable-next-line no-unused-vars
  ease?: string | ((t: number) => number);
  from?: gsap.TweenVars;
  to?: gsap.TweenVars;
  threshold?: number;
  rootMargin?: string;
  textAlign?: React.CSSProperties['textAlign'];
  onAnimationComplete?: () => void;
  as?: 'p' | 'span' | 'div';
}

const StyledSplitText: React.FC<StyledSplitTextProps> = ({
  words,
  className = '',
  delay = 100,
  duration = 0.6,
  ease = 'power3.out',
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  threshold = 0.1,
  rootMargin = '-100px',
  textAlign = 'left',
  onAnimationComplete,
  as = 'span',
}) => {
  const ref = useRef<HTMLElement>(null);
  const animationCompletedRef = useRef(false);
  const scrollTriggerRef = useRef<ScrollTrigger | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !ref.current) return;

    const el = ref.current;
    animationCompletedRef.current = false;

    let splitter: GSAPSplitText;
    try {
      splitter = new GSAPSplitText(el, {
        type: 'words',
        linesClass: 'split-line',
      });
    } catch {
      throw new Error('Failed to create SplitText');
    }

    const targets = splitter.words;
    if (!targets || targets.length === 0) {
      splitter.revert();
      return;
    }

    targets.forEach((t) => {
      (t as HTMLElement).style.willChange = 'transform, opacity';
    });

    const startPct = (1 - threshold) * 100;
    const marginMatch = /^(-?\d+(?:\.\d+)?)(px|em|rem|%)?$/.exec(rootMargin);
    const marginValue = marginMatch ? parseFloat(marginMatch[1]) : 0;
    const marginUnit = marginMatch ? marginMatch[2] || 'px' : 'px';
    const sign =
      marginValue < 0 ? `-=${Math.abs(marginValue)}${marginUnit}` : `+=${marginValue}${marginUnit}`;
    const start = `top ${startPct}%${sign}`;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start,
        toggleActions: 'play none none none',
        once: true,
        onToggle: (self) => {
          scrollTriggerRef.current = self;
        },
      },
      smoothChildTiming: true,
      onComplete: () => {
        animationCompletedRef.current = true;
        gsap.set(targets, {
          ...to,
          clearProps: 'willChange',
          immediateRender: true,
        });
        onAnimationComplete?.();
      },
    });

    tl.set(targets, { ...from, immediateRender: false, force3D: true });
    tl.to(targets, {
      ...to,
      duration,
      ease,
      stagger: delay / 1000,
      force3D: true,
    });

    return () => {
      tl.kill();
      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
        scrollTriggerRef.current = null;
      }
      gsap.killTweensOf(targets);
      if (splitter) {
        splitter.revert();
      }
    };
  }, [words, delay, duration, ease, from, to, threshold, rootMargin, onAnimationComplete]);

  if (as === 'span') {
    return (
      <span
        ref={ref as React.RefObject<HTMLSpanElement>}
        className={`split-parent overflow-hidden inline-block whitespace-normal ${className}`}
        style={{
          textAlign,
          wordWrap: 'break-word',
        }}
      >
        {words.map((word, index) => (
          <span key={index} className={word.className}>
            {word.text}
            {index < words.length - 1 && ' '}
          </span>
        ))}
      </span>
    );
  }

  if (as === 'div') {
    return (
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={`split-parent overflow-hidden inline-block whitespace-normal ${className}`}
        style={{
          textAlign,
          wordWrap: 'break-word',
        }}
      >
        {words.map((word, index) => (
          <span key={index} className={word.className}>
            {word.text}
            {index < words.length - 1 && ' '}
          </span>
        ))}
      </div>
    );
  }

  return (
    <p
      ref={ref as React.RefObject<HTMLParagraphElement>}
      className={`split-parent overflow-hidden inline-block whitespace-normal ${className}`}
      style={{
        textAlign,
        wordWrap: 'break-word',
      }}
    >
      {words.map((word, index) => (
        <span key={index} className={word.className}>
          {word.text}
          {index < words.length - 1 && ' '}
        </span>
      ))}
    </p>
  );
};

export default StyledSplitText;
